import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef } from "react";
import { Box, Text, Flex, IconButton, Container } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { motion, useInView } from "framer-motion";
// Background colors for tag cards (same as SpecialtySection)
const cardBackgroundColors = ["#81A6FF", "#DA7C98", "#70A7AE"];
// Animation keyframes for smooth slide and fade
const slideInFromRight = keyframes `
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;
const slideInFromLeft = keyframes `
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;
const VenueSpecialtySection = ({ description, tags = [], pubColors, cardColor, }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [slideDirection, setSlideDirection] = useState("right");
    const sectionRef = useRef(null);
    const isInView = useInView(sectionRef, { once: true, amount: 0.3 });
    const touchStartX = useRef(null);
    const touchEndX = useRef(null);
    // Minimum swipe distance to trigger navigation (in pixels)
    const minSwipeDistance = 50;
    // Combine description card with tags
    const cards = [
        {
            type: "description",
            heading: "VENUE'S SPECIALTY",
            content: description || "",
        },
        ...(tags && Array.isArray(tags)
            ? tags
                .filter((tag) => tag && tag.tag_name) // Filter out invalid tags
                .map((tag) => ({
                type: "tag",
                heading: tag.tag_name || "",
                content: tag.description || "",
            }))
            : []),
    ];
    const totalCards = cards.length;
    const handleNext = () => {
        setSlideDirection("right");
        setCurrentIndex((prev) => (prev + 1) % totalCards);
    };
    const handlePrev = () => {
        setSlideDirection("left");
        setCurrentIndex((prev) => (prev - 1 + totalCards) % totalCards);
    };
    const handleTouchStart = (e) => {
        touchStartX.current = e.targetTouches[0].clientX;
        touchEndX.current = null;
    };
    const handleTouchMove = (e) => {
        touchEndX.current = e.targetTouches[0].clientX;
    };
    const handleTouchEnd = () => {
        if (!touchStartX.current || !touchEndX.current)
            return;
        const distance = touchStartX.current - touchEndX.current;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;
        if (isLeftSwipe && totalCards > 1) {
            handleNext();
        }
        else if (isRightSwipe && totalCards > 1) {
            handlePrev();
        }
        // Reset values
        touchStartX.current = null;
        touchEndX.current = null;
    };
    if (totalCards === 0) {
        return null;
    }
    const currentCard = cards[currentIndex];
    // Get background color: first card uses purple, tags use cardBackgroundColors
    const getBackgroundColor = (index) => {
        if (index === 0) {
            return (cardColor || "linear-gradient(131.57deg, #9979F5 0%, #4D3398 100%)");
        }
        // For tag cards, cycle through cardBackgroundColors
        const tagIndex = index - 1;
        const tagColor = tags &&
            tags[tagIndex] &&
            tags[tagIndex].color_code &&
            tags[tagIndex].color_code !== ""
            ? tags[tagIndex].color_code
            : null;
        return (tagColor || cardBackgroundColors[tagIndex % cardBackgroundColors.length]);
    };
    const currentBgColor = getBackgroundColor(currentIndex);
    const currentTag = currentIndex > 0 ? tags[currentIndex - 1] : null;
    const currentBgImage = currentTag && currentTag.image && currentTag.image.trim() !== ""
        ? `url(${currentTag.image})`
        : null;
    return (_jsx(Box, { ref: sectionRef, w: "100%", py: 12, position: "relative", overflowX: "hidden", children: _jsx(Container, { maxW: "container.lg", px: 0, children: _jsxs(Box, { as: motion.div, position: "relative", bg: currentBgImage ? "transparent" : currentBgColor, bgImage: currentBgImage || undefined, bgPosition: "center", bgRepeat: "no-repeat", bgSize: "cover", borderRightRadius: { base: "none", md: "20px" }, minH: ["250px", "250px", "250px", "350px"], overflow: "visible", display: "flex", flexDirection: "column", w: "100%", sx: {
                    transition: "background-color 0.5s ease-out",
                    touchAction: "pan-y pinch-zoom",
                }, initial: { opacity: 0, y: 50 }, animate: isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }, 
                // @ts-ignore - framer-motion transition prop
                transition: { duration: 0.6, ease: "easeOut" }, onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, onTouchEnd: handleTouchEnd, children: [_jsx(Box, { position: "absolute", left: 3, top: -3, h: "calc(100% + 36px)", w: "1px", bg: "white", zIndex: 1 }), _jsxs(Flex, { direction: "column", flex: "1", p: 8, pl: 10, pr: 8, position: "relative", zIndex: 2, animation: `${slideDirection === "right" ? slideInFromRight : slideInFromLeft} 0.5s ease-out`, pointerEvents: "auto", w: "100%", maxW: "100%", overflow: "hidden", children: [_jsx(Text, { fontSize: ["2xl", "3xl", "4xl", "5xl"], fontWeight: "500", color: "white", mb: 6, textTransform: "uppercase", letterSpacing: "0.05em", children: currentCard.heading }), _jsx(Text, { fontSize: ["xs", "xs", "sm", "sm"], fontWeight: "400", color: "white", lineHeight: "1.8", flex: "1", whiteSpace: "pre-wrap", children: currentCard.content })] }, currentIndex), totalCards > 1 && (_jsx(Box, { h: "80px", position: "absolute", bottom: "-40px", right: 4, display: "flex", alignItems: "center", justifyContent: "flex-end", zIndex: 10, children: _jsxs(Flex, { gap: 2, children: [_jsx(IconButton, { "aria-label": "Previous", icon: _jsx(ChevronLeftIcon, { boxSize: 6 }), onClick: handlePrev, bg: "white", border: `1px solid #E2E2E2`, color: "black", borderRadius: "10", size: "md", _hover: { bg: "gray.100" }, _active: { bg: "gray.200" }, isDisabled: totalCards <= 1 }), _jsx(IconButton, { "aria-label": "Next", icon: _jsx(ChevronRightIcon, { boxSize: 6 }), onClick: handleNext, bg: "white", border: `1px solid #E2E2E2`, color: "black", borderRadius: "10", size: "md", _hover: { bg: "gray.100" }, _active: { bg: "gray.200" }, isDisabled: totalCards <= 1 })] }) }))] }) }) }));
};
export default VenueSpecialtySection;
